import * as React from "react";
import { Attendee } from "@/utils/AccountStore";

interface TicketRegistrationFormProps {
    initialProfile: Attendee;
    onSubmit: (profile: Attendee) => Promise<void> | void;
    submitting?: boolean;
    error?: string | null;
}

const TicketRegistrationForm: React.FC<TicketRegistrationFormProps> = ({ initialProfile, onSubmit, submitting = false, error }) => {
    const [profile, setProfile] = React.useState<Attendee>(initialProfile);
    const [validationError, setValidationError] = React.useState<string | null>(null);
    const [emailError, setEmailError] = React.useState<string | null>(null);
    const [cellError, setCellError] = React.useState<string | null>(null);

    React.useEffect(() => {
        setProfile(initialProfile);
    }, [initialProfile]);

    const validateEmail = (email: string): boolean => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const validateCellNumber = (cell: string): boolean => {
        // Remove spaces from cell number
        const cleanCell = cell.replace(/\s/g, '');
        // Check if it starts with 0 and has exactly 10 digits
        const cellRegex = /^0\d{9}$/;
        return cellRegex.test(cleanCell);
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        setProfile((prev) => ({ ...prev, [name]: value }));

        // Clear field-specific errors when user starts typing
        if (name === 'email') {
            setEmailError(null);
        } else if (name === 'mobileNumber') {
            setCellError(null);
        }
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        
        // Reset errors
        setValidationError(null);
        setEmailError(null);
        setCellError(null);

        // Check for empty fields
        if (!profile.firstName.trim() || !profile.lastName.trim() || !profile.email.trim() || !profile.mobileNumber.trim()) {
            setValidationError("All fields are required to continue.");
            return;
        }

        // Validate email
        if (!validateEmail(profile.email.trim())) {
            setEmailError("Please enter a valid email address (e.g., example@domain.com)");
            return;
        }

        // Validate cell number
        const cleanCell = profile.mobileNumber.replace(/\s/g, '');
        if (!validateCellNumber(cleanCell)) {
            setCellError("Cell number must start with 0 and contain exactly 10 digits");
            return;
        }

        // All validation passed, submit with cleaned data
        await onSubmit({
            firstName: profile.firstName.trim(),
            lastName: profile.lastName.trim(),
            email: profile.email.trim(),
            mobileNumber: cleanCell, // Strip spaces from cell number
        } as Attendee);
    };

    return (
        <form className="bg-white shadow rounded-lg p-6 space-y-4" onSubmit={handleSubmit}>
            <div>
                <h2 className="text-xl font-semibold mb-1">Complete your registration</h2>
                <p className="text-sm text-gray-600">We need a few details before you can access your tickets.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <label className="flex flex-col text-sm">
                    <span className="font-medium mb-1">First name</span>
                    <input className="border rounded px-3 py-2" type="text" name="firstName" value={profile.firstName} onChange={handleChange} required />
                </label>
                <label className="flex flex-col text-sm">
                    <span className="font-medium mb-1">Last name</span>
                    <input className="border rounded px-3 py-2" type="text" name="lastName" value={profile.lastName} onChange={handleChange} required />
                </label>
                <label className="flex flex-col text-sm">
                    <span className="font-medium mb-1">Email address</span>
                    <input 
                        className={`border rounded px-3 py-2 ${emailError ? 'border-red-500' : ''}`}
                        type="email" 
                        name="email" 
                        value={profile.email} 
                        onChange={handleChange} 
                        required 
                        placeholder="example@domain.com"
                    />
                    {emailError && (
                        <span className="text-xs text-red-600 mt-1">{emailError}</span>
                    )}
                </label>
                <label className="flex flex-col text-sm">
                    <span className="font-medium mb-1">Cell number</span>
                    <input 
                        className={`border rounded px-3 py-2 ${cellError ? 'border-red-500' : ''}`}
                        type="tel" 
                        name="mobileNumber" 
                        value={profile.mobileNumber} 
                        onChange={handleChange} 
                        required 
                        placeholder="0821234567"
                        maxLength={12}
                    />
                    {cellError && (
                        <span className="text-xs text-red-600 mt-1">{cellError}</span>
                    )}
                    <span className="text-xs text-gray-500 mt-1">Format: 0821234567 (10 digits starting with 0)</span>
                </label>
            </div>
            {(validationError || error) && (
                <div className="text-sm text-red-600" role="alert">
                    {validationError ?? error}
                </div>
            )}
            <button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2 rounded" disabled={submitting}>
                {submitting ? "Saving..." : "Save & Continue"}
            </button>
        </form>
    );
};

export default TicketRegistrationForm;
